import { useCallback, useEffect, useRef, useState } from 'react';
import {
	ApplicationsFilter,
	Batch,
	CandidateLoadDetails,
	FunctionaryList,
	GetCandidatesLoadSummaryResponse,
	GetProposerLoadSummaryResponse,
	GetZonesProposerLoadSummaryResponse,
	PagingConfig,
	ProposerFilter,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useAuth } from '../../../hooks/useAuth';
import { useBatchId } from '../../../hooks/useBatchId';
import { useLoadingIndicator } from '../../../hooks/useLoadingIndicator';
import { DEFAULT_PAGE_SIZE } from '../../../shared/Constants';
import { useSnapshot } from 'valtio';
import { ProposerListState } from './ProposerList.store';
import {
	bulkAssignFunctionaryToApplicationForRegion,
	getCandidatesLoadSummary,
	getProposerLoadSummary,
	getZonesProposerLoadSummary,
	listAvailableFunctionaries,
	reassignFunctionaryToApplication,
} from '../../../service/VolunteerApplicationService';
import useQueryParams from '../../../hooks/useQueryParams';
import { useCallApi } from '../../../hooks/useCallApi';
import { useConfirmPopup } from '../../../hooks/useConfirmPopup';
import { useInfoPopup } from '../../../hooks/useInfoPopUp';
import ConfirmTick from '../../../assets/images/confirm-tick.svg';
import {
	PROPOSER_LOAD_DASHBOARD,
	PROPOSER_LOAD_REASSIGN,
	PROPOSER_LOAD_VIEW,
	PROPOSER_LOAD_ZONES,
} from '../../../shared/Routes';
import { useLocation } from 'react-router-dom';
import { useToastMessage } from '../../../hooks/useToastMessage';
import useUserSession from '../../../hooks/useUserSession';
import { displayError, createFetchDeduper } from '../../../shared/Utils';

const proposerLoadSummaryDeduper = createFetchDeduper();
const candidatesLoadSummaryDeduper = createFetchDeduper();
const availableFunctionariesDeduper = createFetchDeduper();
const zonesProposerLoadDeduper = createFetchDeduper();

const EMPTY_PROPOSER_LOAD_SUMMARY: GetProposerLoadSummaryResponse = {
	proposerLoadSummary: [],
	pagination: { totalNoOfRecords: 0 },
};

const EMPTY_CANDIDATES_LOAD_SUMMARY: GetCandidatesLoadSummaryResponse = {
	candidateLoadDetail: [],
	zoneName: '',
	pagination: { totalNoOfRecords: 0 },
};

const EMPTY_AVAILABLE_FUNCTIONARIES: FunctionaryList = {
	functionary: [],
	pagination: { totalNoOfRecords: 0 },
};

const EMPTY_ZONES_PROPOSER_LOAD: GetZonesProposerLoadSummaryResponse = {
	zonesProposerLoadSummary: [],
	pagination: { totalNoOfRecords: 0 },
};

export function resetProposerLoadCache() {
	proposerLoadSummaryDeduper.reset();
	candidatesLoadSummaryDeduper.reset();
	availableFunctionariesDeduper.reset();
	zonesProposerLoadDeduper.reset();

	ProposerListState.proposerLoadSummaryResponse = EMPTY_PROPOSER_LOAD_SUMMARY;
	ProposerListState.candidatesLoadSummaryResponse =
		EMPTY_CANDIDATES_LOAD_SUMMARY;
	ProposerListState.availableFunctionariesResponse =
		EMPTY_AVAILABLE_FUNCTIONARIES;
	ProposerListState.zonesProposerLoadResponse = EMPTY_ZONES_PROPOSER_LOAD;
}

export function resetCandidatesLoadSummaryCache(): void {
	candidatesLoadSummaryDeduper.reset();
	ProposerListState.candidatesLoadSummaryResponse =
		EMPTY_CANDIDATES_LOAD_SUMMARY;
}

export const useProposerLoad = () => {
	const { confirmPopUpAction } = useConfirmPopup();
	const { infoPopUpAction } = useInfoPopup();
	const queryParams = useQueryParams();
	const { srcmId } = useAuth();
	const { selectedRole } = useUserSession();
	const { callApi } = useCallApi();
	let batchId = 0;
	const hasShownErrorToast = useRef(false);

	const selectedBatch = sessionStorage.getItem('SELECTED_BATCH');
	if (selectedBatch) {
		batchId = Number((JSON.parse(selectedBatch) as Batch).id);
	} else {
		const currentActiveBatch = localStorage.getItem('CURRENT_ACTIVE_BATCH');
		if (currentActiveBatch) {
			const parsedData: Batch = JSON.parse(currentActiveBatch) as Batch;
			batchId = Number(parsedData.id);
		}
	}
	const location = useLocation();
	const { showToastMessage } = useToastMessage();

	const filterStateSnap = useSnapshot(ProposerListState);
	const [paging, setPaging] = useState<PagingConfig>(() => {
		return { ...filterStateSnap.paging } as PagingConfig;
	});
	const [applicationRecord, setApplicationRecord] =
		useState<CandidateLoadDetails>();
	const [filter, setFilter] = useState<ApplicationsFilter>({});
	const [proposerfilter, setProposerFilter] = useState<ProposerFilter>({});
	const { setLoading } = useLoadingIndicator();
	const { navigateToRoute } = useBatchId();

	useEffect(() => {
		const storedApplication = sessionStorage.getItem('SELECTED_APPLICATION');

		if (storedApplication) {
			setApplicationRecord(
				JSON.parse(storedApplication) as CandidateLoadDetails
			);
		}
	}, []);

	const applicationsList =
		filterStateSnap.proposerLoadSummaryResponse as GetProposerLoadSummaryResponse;
	const candidatesLoadSummaryList =
		filterStateSnap.candidatesLoadSummaryResponse as GetCandidatesLoadSummaryResponse;
	const functionaryList =
		filterStateSnap.availableFunctionariesResponse as FunctionaryList;
	const zonesProposerLoadDetailList =
		filterStateSnap.zonesProposerLoadResponse as GetZonesProposerLoadSummaryResponse;

	const onPageChange = useCallback((pageNumber: number, pageSize: number) => {
		ProposerListState.paging = { pageNo: pageNumber, pageSize };
		setPaging({ pageNo: pageNumber, pageSize });
	}, []);

	const fetchProposerLoadSummary = useCallback(
		async (pageNumber: number, pageSize: number) => {
			if (!srcmId) return;

			const pageIndex = pageNumber < 1 ? 0 : pageNumber - 1;
			const proposerLoadSummaryRequest = {
				batchId,
				pageIndex,
				pageSize,
				functionarySrcmId: srcmId,
			};

			const fetchKey = `proposerLoadSummary_${JSON.stringify(
				proposerLoadSummaryRequest
			)}`;
			if (proposerLoadSummaryDeduper.shouldSkip(fetchKey)) return;
			proposerLoadSummaryDeduper.markInflight(fetchKey);

			setLoading(true);
			try {
				const applications = await getProposerLoadSummary(
					proposerLoadSummaryRequest
				);
				const normalizedApplications = {
					...applications,
					proposerLoadSummary: applications.proposerLoadSummary?.map(
						(loadSummaryRow) => ({
							...loadSummaryRow,
							totalCandidatesCount: loadSummaryRow.totalCandidatesCount ?? 0,
							maleCandidatesCount: loadSummaryRow.maleCandidatesCount ?? 0,
							femaleCandidatesCount: loadSummaryRow.femaleCandidatesCount ?? 0,
							totalProposersCount: loadSummaryRow.totalProposersCount ?? 0,
							maleProposersCount: loadSummaryRow.maleProposersCount ?? 0,
							femaleProposersCount: loadSummaryRow.femaleProposersCount ?? 0,
						})
					),
				};

				proposerLoadSummaryDeduper.markCompleted(fetchKey);

				if (
					normalizedApplications.pagination.totalNoOfPages &&
					normalizedApplications.pagination.totalNoOfPages > 0 &&
					pageNumber > normalizedApplications.pagination.totalNoOfPages
				) {
					onPageChange(
						normalizedApplications.pagination.totalNoOfPages ?? 1,
						pageSize
					);
				} else {
					ProposerListState.proposerLoadSummaryResponse =
						normalizedApplications;
				}
			} catch (e) {
				proposerLoadSummaryDeduper.markFailed();
				if (e instanceof Error) {
					showToastMessage('error', e.message);
				} else {
					showToastMessage('error', 'Unknown error occurred');
				}
			}
			setLoading(false);
		},
		[batchId, setLoading, srcmId, onPageChange, showToastMessage]
	);

	const fetchCandidatesLoadSummary = useCallback(
		async (
			pageNumber: number,
			pageSize: number,
			applicationsFilter: ApplicationsFilter
		) => {
			if (!srcmId) return;

			const zoneId = Number(queryParams.get('zoneId'));
			const pageIndex = pageNumber < 1 ? 0 : pageNumber - 1;
			const candidateLoadSummaryRequest = {
				zoneId,
				batchId,
				pageIndex,
				applicationsFilter,
				pageSize,
				functionarySrcmId: srcmId,
			};

			const fetchKey = `candidatesLoadSummary_${JSON.stringify(
				candidateLoadSummaryRequest
			)}`;
			if (candidatesLoadSummaryDeduper.shouldSkip(fetchKey)) return;
			candidatesLoadSummaryDeduper.markInflight(fetchKey);

			setLoading(true);
			try {
				const applications = await getCandidatesLoadSummary(
					candidateLoadSummaryRequest
				);

				candidatesLoadSummaryDeduper.markCompleted(fetchKey);

				ProposerListState.candidatesLoadSummaryResponse = applications;
				if (
					applications.pagination.totalNoOfPages &&
					applications.pagination.totalNoOfPages > 0 &&
					pageNumber > applications.pagination.totalNoOfPages
				) {
					onPageChange(applications.pagination.totalNoOfPages ?? 1, pageSize);
				}
			} catch (e) {
				candidatesLoadSummaryDeduper.markFailed();
				if (e instanceof Error) {
					showToastMessage('error', e.message);
				} else {
					showToastMessage('error', 'Unknown error occurred');
				}
			}
			setLoading(false);
		},
		[batchId, setLoading, srcmId, queryParams, onPageChange, showToastMessage]
	);

	const fetchProposerLoadDetail = useCallback(
		async (
			pageNumber: number,
			pageSize: number,
			applicationFilter: ApplicationsFilter
		) => {
			const applicationId = Number(queryParams.get('applicationId'));
			if (!applicationId) return;

			const pageIndex = pageNumber < 1 ? 0 : pageNumber - 1;
			const availableFunctionariesRequest = {
				applicationId,
				pageIndex,
				pageSize,
				filter: applicationFilter,
			};

			const fetchKey = `availableFunctionaries_${JSON.stringify(
				availableFunctionariesRequest
			)}`;
			if (availableFunctionariesDeduper.shouldSkip(fetchKey)) return;
			availableFunctionariesDeduper.markInflight(fetchKey);

			setLoading(true);
			try {
				const response = await listAvailableFunctionaries(
					availableFunctionariesRequest
				);

				availableFunctionariesDeduper.markCompleted(fetchKey);

				if (
					response.pagination.totalNoOfPages &&
					response.pagination.totalNoOfPages > 0 &&
					pageNumber > response.pagination.totalNoOfPages
				) {
					onPageChange(response.pagination.totalNoOfPages ?? 1, pageSize);
				} else {
					ProposerListState.availableFunctionariesResponse = response;
				}
			} catch (e) {
				availableFunctionariesDeduper.markFailed();
				if (!hasShownErrorToast.current) {
					hasShownErrorToast.current = true;
					const errorMessage = displayError(e);
					showToastMessage('error', errorMessage, 'no-proposer-error');
				}
				setTimeout(() => {
					hasShownErrorToast.current = false;
				}, 3000);
			}

			setLoading(false);
		},
		[onPageChange, showToastMessage, setLoading, queryParams]
	);

	const fetchProposerZonesLoadDetail = useCallback(
		async (
			pageNumber: number,
			pageSize: number,
			applicationsFilter: ApplicationsFilter
		) => {
			if (!srcmId) return;

			const regionId = Number(queryParams.get('regionId'));
			const pageIndex = pageNumber < 1 ? 0 : pageNumber - 1;

			const proposerZonesLoadRequest = {
				batchId,
				regionId,
				functionarySrcmId: srcmId,
				pageIndex,
				applicationsFilter,
				pageSize,
			};

			const fetchKey = `zonesProposerLoad_${JSON.stringify(
				proposerZonesLoadRequest
			)}`;
			if (zonesProposerLoadDeduper.shouldSkip(fetchKey)) return;
			zonesProposerLoadDeduper.markInflight(fetchKey);

			setLoading(true);
			try {
				const applications = await getZonesProposerLoadSummary(
					proposerZonesLoadRequest
				);

				zonesProposerLoadDeduper.markCompleted(fetchKey);

				if (
					applications.pagination.totalNoOfPages &&
					applications.pagination.totalNoOfPages > 0 &&
					pageNumber > applications.pagination.totalNoOfPages
				) {
					onPageChange(applications.pagination.totalNoOfPages ?? 1, pageSize);
				} else {
					ProposerListState.zonesProposerLoadResponse = applications;
				}
			} catch (e) {
				zonesProposerLoadDeduper.markFailed();
				if (e instanceof Error) {
					showToastMessage('error', e.message);
				} else {
					showToastMessage('error', 'Unknown error occurred');
				}
			}
			setLoading(false);
		},
		[batchId, onPageChange, setLoading, showToastMessage, srcmId, queryParams]
	);

	const onFilterChange = useCallback(
		(filterParam: ApplicationsFilter) => {
			ProposerListState.filterBy = { ...filterParam };
			setFilter(filterParam);
			onPageChange(1, paging.pageSize ?? DEFAULT_PAGE_SIZE);
		},
		[onPageChange, paging.pageSize]
	);

	const onProposerZoneFilterChange = useCallback(
		(filterParam: ProposerFilter) => {
			ProposerListState.filterBy = { ...filterParam };
			setProposerFilter(filterParam);
			onPageChange(1, paging.pageSize ?? DEFAULT_PAGE_SIZE);
		},
		[onPageChange, paging.pageSize]
	);

	const onConfirmSubmit = useCallback(
		async (functionaryId: number) => {
			if (functionaryId) {
				const applicationId = Number(queryParams.get('applicationId'));
				try {
					await callApi(
						() =>
							reassignFunctionaryToApplication({
								applicationId,
								functionaryId,
								selectedRoleCode: selectedRole as UserRole,
							}),
						'errorOccurredWhileSubmittingApplication'
					);
					resetProposerLoadCache();
					infoPopUpAction(
						'successfullyUpdated',
						'proposerReassignedSuccessfully',
						() => {
							navigateToRoute(PROPOSER_LOAD_VIEW);
						},
						ConfirmTick
					);
				} catch (e) {
					//handled in callApi
				}
			}
		},
		[queryParams, callApi, infoPopUpAction, selectedRole, navigateToRoute]
	);

	const onSubmitClick = useCallback(
		(funcId?: number) => {
			if (funcId) {
				confirmPopUpAction(
					'confirmAssign',
					'areYouSureYouWantAssignTheProposer',
					() => onConfirmSubmit(funcId),
					'confirm'
				);
			}
		},
		[onConfirmSubmit, confirmPopUpAction]
	);

	const onConfirmBulkSubmit = useCallback(
		async (
			regionIds: Array<number>,
			zoneIds: Array<number>,
			route: string,
			isRegion?: boolean
		) => {
			if ((regionIds && batchId) || (zoneIds && batchId)) {
				try {
					await callApi(
						() =>
							bulkAssignFunctionaryToApplicationForRegion({
								regionIds,
								batchId,
								zoneIds,
							}),
						'errorOccurredWhileSubmittingApplication'
					);
					resetProposerLoadCache();
					infoPopUpAction(
						'successfullyUpdated',
						isRegion
							? 'regionLevelBulkAssignProcessing'
							: 'proposerBulkReassignedSuccessfully',
						() => {
							navigateToRoute(route);
						},
						ConfirmTick
					);
				} catch (e) {
					//handled in callApi
				}
			}
		},
		[navigateToRoute, callApi, infoPopUpAction, batchId]
	);

	const onBulkAssign = useCallback(
		(
			regionIds: number[],
			zoneIds: number[],
			route: string,
			isRegion?: boolean
		) => {
			confirmPopUpAction(
				'confirmBulkAssign',
				'areYouSureYouWantRuntheBulkAssign',
				() => onConfirmBulkSubmit(regionIds, zoneIds, route, isRegion),
				'confirm'
			);
		},
		[onConfirmBulkSubmit, confirmPopUpAction]
	);

	useEffect(() => {
		if (location.pathname.includes(PROPOSER_LOAD_DASHBOARD)) {
			void fetchProposerLoadSummary(
				paging.pageNo ?? 1,
				paging.pageSize ?? DEFAULT_PAGE_SIZE
			);
		}

		if (location.pathname.includes(PROPOSER_LOAD_VIEW)) {
			void fetchCandidatesLoadSummary(
				paging.pageNo ?? 1,
				paging.pageSize ?? DEFAULT_PAGE_SIZE,
				filter
			);
		}

		if (location.pathname.includes(PROPOSER_LOAD_ZONES)) {
			void fetchProposerZonesLoadDetail(
				paging.pageNo ?? 1,
				paging.pageSize ?? DEFAULT_PAGE_SIZE,
				filter
			);
		}

		if (location.pathname.includes(PROPOSER_LOAD_REASSIGN)) {
			void fetchProposerLoadDetail(
				paging.pageNo ?? 1,
				paging.pageSize ?? DEFAULT_PAGE_SIZE,
				filter
			);
		}
	}, [
		paging,
		filter,
		proposerfilter,
		fetchProposerLoadSummary,
		fetchProposerLoadDetail,
		fetchCandidatesLoadSummary,
		fetchProposerZonesLoadDetail,
		location,
	]);

	return {
		applicationsList,
		candidatesLoadSummaryList,
		onPageChange,
		filter,
		onFilterChange,
		paging,
		onProposerZoneFilterChange,
		proposerfilter,
		functionaryList,
		zonesProposerLoadDetailList,
		onSubmitClick,
		onConfirmSubmit,
		onBulkAssign,
		applicationRecord,
	};
};
